/**
 * Quote-comparison arithmetic for the RFQ detail grid. Pure so it can be
 * unit-tested; all money is integer minor units (paise). Line math is
 * delegated to the PO line helper (`computePoLine`): subtotal − discount, GST
 * on the net. One difference from PO lines: a FLAT quote discount is **per
 * unit** (off the unit price), so it is scaled by quantity before hand-off.
 */
import type { RfqItem, RfqSupplier } from './types';
import type { PoDiscountType } from '@/features/purchase-orders';
import { computePoLine, type PoLineTotals } from '@/features/purchase-orders/line-totals';

export interface QuoteCell extends PoLineTotals {
  unit_cost: number;
  gst_rate: number;
  discount_type: PoDiscountType;
  /** basis points for PERCENTAGE, minor units per unit for FLAT */
  discount_value: number;
}

/** `tenantCurrency` picks the tax regime (an RFQ is in the tenant currency). */
export function quoteCell(item: RfqItem, supplier: RfqSupplier, tenantCurrency?: string): QuoteCell | null {
  const q = supplier.quotes.find((x) => x.rfq_item_id === item.id);
  if (!q) return null;
  const discount_type = q.discount_type ?? 'FLAT';
  const discount_value = q.discount_value ?? 0;
  return {
    unit_cost: q.unit_cost,
    gst_rate: q.gst_rate,
    discount_type,
    discount_value,
    ...computePoLine({
      quantity: item.quantity,
      unit_cost: q.unit_cost,
      gst_rate: q.gst_rate,
      discount_type,
      // FLAT is per unit on a quote; the PO helper expects the line amount.
      discount_value: discount_type === 'FLAT' ? Math.floor(item.quantity * discount_value) : discount_value,
    }, tenantCurrency),
  };
}

/** rfq_supplier ids with the lowest line_total for an item (ties included); [] when no one quoted. */
export function lowestForItem(item: RfqItem, suppliers: RfqSupplier[], tenantCurrency?: string): string[] {
  let best = Infinity;
  const winners: string[] = [];
  for (const s of suppliers) {
    const c = quoteCell(item, s, tenantCurrency);
    if (!c) continue;
    if (c.line_total < best) { best = c.line_total; winners.length = 0; winners.push(s.id); }
    else if (c.line_total === best) winners.push(s.id);
  }
  return winners;
}

/**
 * Starting award per line (QA PU1-003): the lowest quoted total, first supplier
 * in column order on a tie; '' (not awarded) when no one quoted the line.
 */
export function defaultAwards(items: RfqItem[], suppliers: RfqSupplier[], tenantCurrency?: string): Record<string, string> {
  return Object.fromEntries(items.map((i) => [i.id, lowestForItem(i, suppliers, tenantCurrency)[0] ?? '']));
}

export interface AwardGroup {
  supplier: RfqSupplier;
  lines: { item: RfqItem; cell: QuoteCell }[];
  total: number;
}

/** The POs an award will raise: one group per winning supplier, in column order, with only its lines. */
export function awardSummary(
  items: RfqItem[], suppliers: RfqSupplier[], awards: Record<string, string>, tenantCurrency?: string,
): AwardGroup[] {
  const groups: AwardGroup[] = [];
  for (const supplier of suppliers) {
    const lines = items
      .filter((i) => awards[i.id] === supplier.id)
      .map((item) => ({ item, cell: quoteCell(item, supplier, tenantCurrency) }))
      .filter((l): l is { item: RfqItem; cell: QuoteCell } => l.cell !== null);
    if (lines.length > 0) groups.push({ supplier, lines, total: lines.reduce((t, l) => t + l.cell.line_total, 0) });
  }
  return groups;
}

/** Sum of a supplier's quoted line totals + how many of the RFQ's lines they covered. */
export function supplierTotal(items: RfqItem[], supplier: RfqSupplier, tenantCurrency?: string): { total: number; quoted_lines: number } {
  let total = 0;
  let quoted = 0;
  for (const item of items) {
    const c = quoteCell(item, supplier, tenantCurrency);
    if (!c) continue;
    total += c.line_total;
    quoted += 1;
  }
  return { total, quoted_lines: quoted };
}
